import { NetworkingService } from "../../networkingService/NetworkingService";

/**
 * Response from `coupleWithPartnerV2` callable.
 */
export interface CoupleToPartnerResponse {
  partnerIdentifier?: string;
  partnerAppVersion?: string;
}

export interface InvitationCodePolicyResponse {
  generationAlphabet: string;
  generationLength: number;
  acceptedInputPattern: string;
  normalization: {
    trimWhitespace: boolean;
    uppercase: boolean;
    removeWhitespaceAndHyphen: boolean;
  };
}

/**
 * PartnershipAPIService handles Firebase callable function requests
 * for partnership-related operations that require backend processing.
 */
export class PartnershipAPIService {
  private static instance: PartnershipAPIService;
  private networkingService = NetworkingService.getInstance();
  private invitationCodePolicyCache: InvitationCodePolicyResponse | null = null;

  private constructor() {}

  public static getInstance(): PartnershipAPIService {
    if (!PartnershipAPIService.instance) {
      PartnershipAPIService.instance = new PartnershipAPIService();
    }
    return PartnershipAPIService.instance;
  }

  /**
   * Couples the current user to a partner using an invitation code.
   * Calls the backend Cloud Function to validate and establish the partnership.
   *
   * @param invitationCode The partner's invitation code
   * @returns Promise resolving to partner information
   */
  public async coupleToPartner(
    invitationCode: string,
  ): Promise<CoupleToPartnerResponse> {
    return this.networkingService.callCallable<
      CoupleToPartnerResponse,
      { invitationCode: string }
    >("coupleWithPartnerV2", { invitationCode });
  }

  /**
   * Decides on a partnership request (accept or decline).
   * Calls the backend Cloud Function to process the decision.
   *
   * @param requestingPartnerIdentifier The identifier of the user who sent the request
   * @param isAccepted Whether to accept (true) or decline (false) the request
   * @returns Promise that resolves when the decision is processed
   */
  public async decidePartnershipRequest(
    requestingPartnerIdentifier: string,
    isAccepted: boolean,
  ): Promise<void> {
    await this.networkingService.callCallable<
      { status: string },
      { requestingPartnerIdentifier: string; isAccepted: boolean }
    >("decidePartnershipRequestV2", {
      requestingPartnerIdentifier,
      isAccepted,
    });
  }

  /**
   * Disconnects from a partner.
   * Calls the backend Cloud Function to invalidate the partnership.
   *
   * @param partnerIdentifier The partner's user identifier
   * @param partnershipIdentifier The partnership identifier
   * @returns Promise that resolves when disconnection is complete
   */
  public async disconnectFromPartner(
    partnerIdentifier: string,
    partnershipIdentifier: string,
  ): Promise<void> {
    await this.networkingService.callCallable<
      { status: string },
      { disconnedPartnerIdentifier: string; partnershipIdentifier: string }
    >("disconnectFromPartnerV2", {
      disconnedPartnerIdentifier: partnerIdentifier,
      partnershipIdentifier,
    });
  }

  public async getInvitationCodePolicy(): Promise<InvitationCodePolicyResponse | null> {
    if (this.invitationCodePolicyCache) {
      return this.invitationCodePolicyCache;
    }
    try {
      const policy =
        await this.networkingService.callCallable<InvitationCodePolicyResponse>(
          "getInvitationCodePolicy",
        );
      this.invitationCodePolicyCache = policy;
      return policy;
    } catch {
      return null;
    }
  }
}
